import type { WavespeedModelConfig } from '../types';
import { clampTo, resolveImageSize } from '../../tools';

export const bytedanceConfigs: Record<string, WavespeedModelConfig> = {
    'bytedance/seedream-v5.0-pro': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v5.0-pro
        apiPath: '/bytedance/seedream-v5.0-pro',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg'] as const,
                    'png',
                ),
            };
        },
    },
    'bytedance/seedream-v5.0-pro/edit': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v5.0-pro/edit
        apiPath: '/bytedance/seedream-v5.0-pro/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: aspectRatio === 'match' ? undefined : clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                // 图片 URL
                images: image_urls.slice(0, 10),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg'] as const,
                    'png',
                ),
            };
        },
    },
    'bytedance/seedream-v5.0-flash': {
        // https://wavespeed.ai/models/bytedance/seedream-v5.0-flash
        apiPath: '/bytedance/seedream-v5.0-flash',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '1.5k', '2k'] as const, '1k'),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg'] as const,
                    'png',
                ),
            };
        },
    },
    'bytedance/seedream-v5.0-flash/edit': {
        // https://wavespeed.ai/models/bytedance/seedream-v5.0-flash/edit
        apiPath: '/bytedance/seedream-v5.0-flash/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                aspect_ratio: aspectRatio === 'match' ? undefined : clampTo(aspectRatio || '1:1', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '1:1'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '1.5k', '2k'] as const, '1k'),
                // 图片 URL
                images: image_urls.slice(0, 10),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg'] as const,
                    'png',
                ),
            };
        },
    },
    'bytedance/seedream-v5.0-lite': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v5.0-lite
        apiPath: '/bytedance/seedream-v5.0-lite',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                size: resolveImageSize(
                    clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                    `seedream-5.0-${clampTo((image_resolution || '2K').toUpperCase(), ['2K', '3K', '4K'] as const, '2K')}`,
                ),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg'] as const,
                    'png',
                ),
            };
        },
    },
    'bytedance/seedream-v5.0-lite/edit': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v5.0-lite/edit
        apiPath: '/bytedance/seedream-v5.0-lite/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                    `seedream-5.0-${clampTo((image_resolution || '2K').toUpperCase(), ['2K', '3K', '4K'] as const, '2K')}`,
                ),
                // 图片 URL
                images: image_urls.slice(0, 14),
                // 可选参数
                output_format: clampTo(
                    providerOptions?.output_format || 'png',
                    ['png', 'jpeg'] as const,
                    'png',
                ),
            };
        },
    },
    'bytedance/seedream-v4.5': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v4.5
        apiPath: '/bytedance/seedream-v4.5',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution } = params;
            return {
                prompt: prompt,
                size: resolveImageSize(
                    clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                    `seedream-4.5-${clampTo((image_resolution || '2K').toUpperCase(), ['2K', '4K'] as const, '2K')}`,
                ),
            };
        },
    },
    'bytedance/seedream-v4.5/edit': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v4.5/edit
        apiPath: '/bytedance/seedream-v4.5/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                    `seedream-4.5-${clampTo((image_resolution || '2K').toUpperCase(), ['2K', '4K'] as const, '2K')}`,
                ),
                // 图片 URL
                images: image_urls.slice(0, 14),
            };
        },
    },
    'bytedance/seedream-v4': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v4
        apiPath: '/bytedance/seedream-v4',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, image_resolution } = params;
            return {
                prompt: prompt,
                size: resolveImageSize(
                    clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                    `seedream-4.0-${clampTo((image_resolution || '1K').toUpperCase(), ['1K', '2K', '4K'] as const, '1K')}`,
                ),
            };
        },
    },
    'bytedance/seedream-v4/edit': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedream-v4/edit
        apiPath: '/bytedance/seedream-v4/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution } = params;
            return {
                prompt: prompt,
                size: aspectRatio === 'match' ? undefined : resolveImageSize(
                    clampTo(aspectRatio || '1:1', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '1:1'),
                    `seedream-4.0-${clampTo((image_resolution || '1K').toUpperCase(), ['1K', '2K', '4K'] as const, '1K')}`,
                ),
                // 图片 URL
                images: image_urls.slice(0, 14),
            };
        },
    },
};